<template>
  <article>
    <activity-stage
      :customRootContainerClass="`${keyboardEnabled ? '' : 'full-height'}`"
      :customContentContainerStyles="{
        height: `calc(100% - ${isChromeOS ? 120 : isMobile ? 70 : 53}px)`
      }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" style="height: 100%">
        <div class="mrs-news-syllables-container">
          <div class="left">
            <div class="chalkboard-text-container">
              <span v-text="fullWordDisplay"></span>
            </div>
            <img
              alt="Chalkboard teacher image"
              id="chalkboard-teacher-image"
              :src="chalkBoardTeacherImage"
            />

            <div :class="{ 'selection-container': true, isMobile }" v-show="syllablesLeft === 0">
              <div class="definitions-container">
                <div
                  :class="{ 'format-instructions-text': true, isMobile }"
                  v-for="(action, idx) in definitionActions"
                  :key="idx"
                  :track-by="idx"
                >
                  <span v-text="`${idx + 1}. `"></span>
                  <span :class="action.class">{{ action.instruction | capitalize }}&nbsp;</span>
                  <span v-text="action.text"></span>
                </div>
              </div>

              <div class="actions-container">
                <TextFormattingButtons :options="optionsTextFormattingButtons" />
                <div class="solve-all-button-container" :transition="innerTransition" aria-live="assertive" role="alert">
                  <button
                    @click="solveAllFormattings"
                    class="solve-all-button pure-button"
                    tabindex="0"
                  >
                    <span>Solve All</span>
                  </button>
                </div>
              </div>

              <div class="text-to-format-container">
                <div
                  v-for="(word, idx) in definitionActions"
                  :class="{ 'text-to-format': true, isMobile }"
                  :id="word.id"
                  :track-by="idx"
                  :key="idx"
                >
                  <span
                    @mousedown="enableSelection"
                    @mouseleave="saveSelection"
                    v-text="word.text"
                  >
                  </span>
                </div>
                <div
                  v-if="activityErrorMsg"
                  aria-live="polite"
                  role="alert"
                  style="display: inline-block"
                >
                  <red-x-solid-icon height="26" width="27"></red-x-solid-icon>
                  <span class="error">{{ activityErrorMsg }}</span>
                </div>
              </div>

              <div class="facts-stage">
                <div
                  v-for="(factStep, idx) in factSteps"
                  :class="factStep.stepType"
                  :key="idx"
                  :track-by="idx"
                >
                  <blinking-cursor-text :text-step="idx + syllables.length" :text="factStep.answer">
                  </blinking-cursor-text>
                </div>
              </div>
            </div>
          </div>

          <ul class="right">
            <li v-for="(syllable, idx) in syllables" :key="idx" :track-by="idx">
              <blinking-cursor-text :text-step="idx" :text="syllable.answer">
              </blinking-cursor-text>
            </li>
          </ul>

          <div id="chalkboard"></div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { createNamespacedHelpers } from 'vuex';

import chalkBoardTeacherImage from 'assets/images/activities/mrs-news-syllables/chalkboard-teacher.jpg';

import ActivityStage from 'components/activity/ActivityStage.vue';
import TextFormattingButtons from 'components/buttons/TextFormattingButtons.vue';
import RedXSolidIcon from 'components/icons/RedXSolidIcon.vue';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import {
  MrsNewsSyllablesFormattingTool,
  MrsNewsSyllablesStepType
} from 'graph/types/schema-generated-types';

const application = createNamespacedHelpers('application');

const getDefinitionActions = formattedText => {
  const actions = {
    [MrsNewsSyllablesFormattingTool.BOLD]: {
      instruction: 'bold',
      class: 'bold-formatting',
      id: 'bold-action'
    },
    [MrsNewsSyllablesFormattingTool.ITALIC]: {
      instruction: 'italicize',
      class: 'italic-formatting',
      id: 'italic-action'
    },
    [MrsNewsSyllablesFormattingTool.UNDERLINE]: {
      instruction: 'underline',
      class: 'underline-formatting',
      id: 'underline-action'
    }
  };

  return formattedText.map(text => ({
    isSolved: false,
    text: text.text.trim(),
    ...actions[text.formattingTool]
  }));
};

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    BlinkingCursorText,
    RedXSolidIcon,
    TextFormattingButtons
  },

  computed: {
    ...application.mapState({
      isMobile: state => state.device.isMobile
    }),
    optionsTextFormattingButtons() {
      if (!this.currentStep) {
        return null;
      }
      return {
        boldCallback: event => {
          if (event.pointerType !== 'mouse') this.saveSelection();
          this.handleAction('bold');
          this.disableSelection();
        },
        italicCallback: event => {
          if (event.pointerType !== 'mouse') this.saveSelection();
          this.handleAction('italicize');
          this.disableSelection();
        },
        underlineCallback: event => {
          if (event.pointerType !== 'mouse') this.saveSelection();
          this.handleAction('underline');
          this.disableSelection();
        }
      };
    }
  },

  data() {
    return {
      voice: null,
      solvedAll: false,
      fullWordDisplay: null,
      activityName: 'MrsNewsSyllables',
      actionsLeft: null,
      syllables: [],
      syllablesLeft: null,
      definitionText: '',
      definitionActions: [],
      textWords: [],
      factSteps: [],
      factsIndex: 0,
      chalkBoardTeacherImage,
      activityErrorMsg: ''
    };
  },
  methods: {
    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
    },

    solveAllFormattings() {
      if (this.solvedAll) return;
      this.solvedAll = true;
      this.sndBell();
      ['bold', 'italicize', 'underline'].forEach(action => this.handleAction(action, true));
    },

    handleAction(action, solveAll = false) {
      const defAction = this.definitionActions.find(def => {
        if (def.isSolved) return false;
        return solveAll
          ? def.instruction === action
          : def.instruction === action && def.text === this.textSelection;
      });

      const errorMessage = this.definitionActions.reduce((agg, def) => {
        if (agg || def.isSolved) return agg;

        return action === def.instruction
          ? 'Incorrect text selection.'
          : 'Incorrect formatting button.';
      }, '');

      if (!defAction && !solveAll) {
        this.activityErrorMsg = errorMessage;
        this.textSelection = '';
        return;
      }

      if (!solveAll) {
        this.sndBell();
        defAction.isSolved = true;
      }

      this.activityErrorMsg = '';

      if (defAction?.id) {
        // append formatting class (e.g. bold, italic)
        document.getElementById(defAction.id).classList.add(defAction.class);
      }

      this.actionsLeft -= 1;
      if (!this.actionsLeft) {
        const elemToAnimate =
          document.querySelector(`.${MrsNewsSyllablesStepType.DEFINITION}`) ||
          document.querySelector(`.${MrsNewsSyllablesStepType.SENTENCE}`);

        if (elemToAnimate) {
          this.setInstructions({
            written: 'Type the definition.',
            voice: `${this.voiceInstructionsPrefix}/mrsnewssyllables-${this.voice}-3.mp3`
          });
          this.showKeyboard({ enable: true });
          TweenMax.to(elemToAnimate, 0.5, { opacity: 1 });
        } else {
          this.handleRoundEnd();
        }
      }
    },

    enableSelection(event) {
      this.disableSelection();

      const ev = event;
      if (ev.target.className.indexOf('selectable') === -1) {
        ev.target.className = 'selectable';
      }
    },

    disableSelection() {
      const selectableNodes = document.getElementsByClassName('selectable');

      if (selectableNodes.length) {
        Array.from(selectableNodes).forEach(node => {
          node.setAttribute('class', '');
        });
      }

      window.getSelection().empty();
    },

    saveSelection() {
      this.textSelection = window
        .getSelection()
        .toString()
        .trim();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      const { formattedText, steps } = rounds[0];

      this.definitionActions = getDefinitionActions(formattedText);
      this.factSteps = steps.filter(s => s.stepType !== MrsNewsSyllablesStepType.SYLLABLE);
      this.syllables = steps.filter(s => s.stepType === MrsNewsSyllablesStepType.SYLLABLE);
      this.syllablesLeft = this.syllables.length;
      this.actionsLeft = this.definitionActions.length;
      this.factsIndex = this.syllables.length;
      this.voice = this.getRandomVoice();

      return {
        rounds,
        instructions: {
          voice: `${this.voiceInstructionsPrefix}/mrsnewssyllables-${this.voice}-1.mp3`,
          written: 'Type the syllables.'
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();

      this.$nextTick(() => {
        this.setActivityContainerHeight();
        this.setChalkboardImgHeight();
        this.setSelectionContainerLocation();
      });

      this.errorLoggerStart();
    },

    handleCorrectKey(attempt) {
      const { stepType, answer } = this.currentStep;

      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        if (this.syllablesLeft) {
          this.fullWordDisplay = answer;
          this.syllablesLeft -= 1;
          if (!this.syllablesLeft) {
            this.hideKeyboard();

            setTimeout(() => {
              // auto format words if on tablet
              if (this.isMobile) {
                this.solveAllFormattings();
              } else {
                this.startSolveAllCountdown();
              }
            }, 5500);

            this.setInstructions({
              written: 'Bold the word. Italicize the part of speech.',
              voice: `${this.voiceInstructionsPrefix}/mrsnewssyllables-${this.voice}-2.mp3`
            });
          }
        } else if (stepType === MrsNewsSyllablesStepType.DEFINITION) {
          this.handleFactDefinitionStep();
        } else if (stepType === MrsNewsSyllablesStepType.SENTENCE) {
          this.handleRoundEnd();
        }
      }
    },

    handleFactDefinitionStep() {
      const sentence = document.querySelector(`.${MrsNewsSyllablesStepType.SENTENCE}`);

      if (sentence) {
        TweenMax.to(sentence, 0.5, { opacity: 1 });
      } else {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      TweenMax.delayedCall(1, this.playCongrats);
      TweenMax.delayedCall(1.5, this.hideKeyboard);
      TweenMax.delayedCall(2, () => this.completeActivity(false));
    },

    startSolveAllCountdown() {
      const interval = 20;
      TweenMax.delayedCall(interval, () => {
        TweenMax.to('.solve-all-button-container', 1, { autoAlpha: 1 });
      });
    },

    setChalkboardImgHeight() {
      const chalkBoardTeacherEl = document.querySelector('#chalkboard-teacher-image');

      if (chalkBoardTeacherEl) {
        chalkBoardTeacherEl.style.height =
          this.isChromeOS || this.isMobile ? `${chalkBoardTeacherEl.clientHeight}px` : '441px';
      }
    },

    setSelectionContainerLocation() {
      const chalkBoardTeacherEl = document.querySelector('#chalkboard-teacher-image');
      const selectionContainerEl = document.querySelector('.selection-container');

      if (chalkBoardTeacherEl && selectionContainerEl) {
        selectionContainerEl.style.top =
          this.isChromeOS || this.isMobile
            ? `calc(${chalkBoardTeacherEl.clientHeight}px / 2 + 16px)`
            : '236.5px';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.mrs-news-syllables-container {
  display: flex;
  flex-flow: row nowrap;
  height: 100%;
}

.left,
.right {
  display: inline-block;
  font-family: hwtfutura;
  user-select: none;
}

.left {
  height: 100%;

  img {
    height: 100%;
    width: auto;
  }
}

.right {
  list-style: none;
  margin: 0px;
}

.DEFINITION,
.SENTENCE {
  opacity: 0;
  font-size: 18px;
  letter-spacing: 2px;
}

.selectable {
  user-select: text;
  -moz-user-select: text;
  -webkit-user-select: text;

  &::selection {
    background-color: #000;
    color: #fff;
  }
}

.left {
  position: relative;
  width: 675px;
}

.definitions-container,
.actions-container {
  width: 500px;
}

.format-instructions-text {
  display: inline-block;
  margin-right: 10px;
  margin-bottom: 15px;
}

.format-instructions-text.isMobile {
  margin-bottom: 5px;
}

.actions-container {
  position: relative;

  .solve-all-button-container {
    visibility: hidden;
  }

  .solve-all-button {
    background-color: black;
    border-radius: 6px;
    bottom: 12.5px;
    cursor: pointer;
    font-family: 'hwtfutura-semibold';
    height: 33px;
    left: 200px;
    padding: 6px 5px;
    position: absolute;
    min-width: 77px;
    text-align: center;

    span {
      font-size: 16px;
      color: white;
      user-select: none;
    }
  }

  .text-format-button {
    display: inline-block;
    cursor: pointer;
    margin-right: 20px;
  }
}

.chalkboard-text-container {
  position: absolute;
  left: 250px;
  top: 120px;
  font-size: 35px;
}

.selection-container {
  position: absolute;
  width: 750px;
  left: 195px;
  top: 233px;

  .bold-formatting {
    font-family: 'hwtfutura-bold';
  }

  .italic-formatting {
    font-style: italic;
  }

  .underline-formatting {
    text-decoration: underline;
  }
}

.text-to-format-container {
  .text-to-format {
    margin-right: 30px;
    font-size: 35px;
    display: inline-block;
  }

  .text-to-format.isMobile {
    font-size: 25px !important;
  }
}

.right {
  vertical-align: top;
  font-size: 30px;
  padding: 30px 0 0 20px;
}

.error {
  color: red;
  padding: 0px 10px;
}

.chrome-os {
  .selection-container {
    top: 160px;
    width: 775px;
  }

  .chalkboard-text-container {
    left: 160px;
    top: 80px;
  }
}

.tablet {
  .selection-container {
    top: 175px;
    width: 775px;
  }

  .chalkboard-text-container {
    left: 175px;
    top: 90px;
  }
}
</style>
